/**
 * Before/After Results Component
 * Displays product transformation results with images
 */

'use client';

import styled from '@emotion/styled';
import { useTheme } from '@template-project/afrodita-components';
import { ProductBeforeAfterResult } from '@/domain/entities/product.entity';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
 

export interface BeforeAfterResultsProps {
  title?: string;
  subtitle?: string;
  results: ProductBeforeAfterResult[];
  hideSubtitleOnMobile?: boolean;
}

interface RevealProps {
  $visible: boolean;
}

const RevealWrapper = styled.div<RevealProps>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(20px)')};
  transition: opacity 0.35s ease, transform 0.35s ease;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

const Section = styled.section`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  padding: ${({ theme }) => `${theme.space['2xl']} 0`};
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `0 ${theme.space.md}`};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading2.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-align: center;
  margin: ${({ theme }) => `0 0 ${theme.space.md}`};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: ${({ theme }) => theme.typography.variants.heading4.fontSize}px;
  }
`;

const Description = styled.p<{ hideOnMobile?: boolean }>`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  line-height: 1.6;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'block')};
    font-size: ${({ theme }) => theme.fontSizes[3]}px;
  }
`;

const ResultWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.lg};
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadow.sm};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 75%; /* 4:3 aspect ratio */
  background-color: ${({ theme }) => theme.colors.background.secondary};
  overflow: hidden;
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Details = styled.p`
  padding: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  text-align: center;
`;

export function BeforeAfterResults({
  title = 'Results',
  subtitle,
  results,
  hideSubtitleOnMobile = false,
}: BeforeAfterResultsProps) {
  const theme = useTheme();
  const { ref, isVisible } = useScrollReveal();

  if (!results || results.length === 0) {
    return null;
  }

  return (
    <Section>
      <Container>
        <RevealWrapper ref={ref} $visible={isVisible}>
        <Title>{title}</Title>
        {subtitle && (
          <Description hideOnMobile={hideSubtitleOnMobile}>
            {subtitle}
          </Description>
        )}
        <ResultWrapper>
          {results.map(({ id, image, image_title, name }) => (
            <Card key={id}>
              {image && (
                <ImageContainer>
                  <StyledImage
                    alt={image_title || name || 'Before and After Result'}
                    src={image}
                  />
                </ImageContainer>
              )}
              {(image_title || name) && (
                <Details>{image_title || name}</Details>
              )}
            </Card>
          ))}
        </ResultWrapper>
        </RevealWrapper>
      </Container>
    </Section>
  );
}

